import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  TrendingDown,
  Share2,
  Compass,
  FileText,
  Search,
  Phone,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
  PlayCircle,
  Mail,
  MessageSquare,
  Heart,
  ShoppingBag,
  BarChart3,
  Briefcase,
  Crown,
  Star,
  Store,
  RefreshCw,
  MapPin,
  Plus,
  Minus,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import ClientLogosWall from '../../components/sections/ClientLogosWall';
import HubSpotModal from '../../components/ui/hubspot-modal';
import { BreadcrumbSchema, FAQPageSchema } from '../../lib/utils/schema-utils';

const PAGE_URL = 'https://recruteurs.io/cabinet-recrutement-aix-en-provence/';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

const faqItems = [
  {
    question: 'Recrutez-vous uniquement à Aix-en-Provence ?',
    answer:
      "Notre périmètre principal est le Pays d'Aix : centre-ville, Les Milles, Rousset (Pôle technologique), Gardanne, Pertuis, Vitrolles. Nous intervenons aussi sur Marseille, Salon-de-Provence et l'ensemble de la PACA selon la nature de la mission. Pour les postes de direction et profils rares, nous menons des recherches nationales.",
  },
  {
    question: 'Quels profils recrutez-vous à Aix-en-Provence ?',
    answer:
      "Cadres et managers, commerciaux (terrain, KAM, business developers), profils techniques et IT (le Pôle Rousset et Les Milles concentrent un tissu tech important), fonctions support (RH, finance, ADV), retail et services. Nous travaillons sur des CDI, des management de transition et des missions confidentielles de chasse de tête.",
  },
  {
    question: 'Quelle est votre valeur ajoutée par rapport à un cabinet classique ?',
    answer:
      "Notre Plateforme Parcours Découverte. Avant le premier entretien, chaque candidat traverse un environnement digital sur-mesure qui lui montre la vraie réalité de votre entreprise — culture, équipe, mission, grille de rémunération. Le candidat décide en connaissance de cause. Vous éliminez les no-show, vous divisez la casse des 90 premiers jours, vous fidélisez à 12 mois.",
  },
  {
    question: 'Cabinet de recrutement Aix-en-Provence pour PME : par où commencer ?',
    answer:
      "Le point de départ est un appel de cadrage gratuit de 30 minutes. Nous identifions la nature du besoin (poste critique à l'unité, volume récurrent, campagne marque employeur), les contraintes (timing, budget, secteur), et nous vous orientons. Pour une PME aixoise 30-150 collaborateurs, le format \"recrutement à l'unité avec Plateforme Parcours Découverte\" sécurise mieux le résultat sans engager une ligne budgétaire massive.",
  },
  {
    question: 'Cabinet de recrutement Aix-en-Provence IT et tech : quelles spécificités ?',
    answer:
      "Le Pôle d'Activités d'Aix-Marseille (Les Milles, Rousset) concentre un écosystème tech significatif : éditeurs SaaS, ingénierie, deeptech, services numériques. La concurrence sur les développeurs, ingénieurs DevOps et data engineers est tendue. Notre approche combine chasse de tête senior et IA appliquée au sourcing pour atteindre les profils passifs. La Plateforme Parcours Découverte — qui montre la stack, l'équipe, le quotidien réel avant l'entretien — fait la différence sur ces profils sur-sollicités.",
  },
  {
    question: 'Comment se déroule une mission concrètement ?',
    answer:
      "Brief approfondi (contexte, équipe, attendus mesurables) → ciblage et sourcing direct → qualification motivationnelle → Plateforme Parcours Découverte → présentation d'un dossier complet (pas une liste de CV) → accompagnement jusqu'à la signature et au-delà avec un suivi 90 jours. Tout est documenté, mesuré, traçable.",
  },
  {
    question: 'Intervenez-vous sur des postes de cadres dirigeants à Aix ?',
    answer:
      "Oui, en chasse de tête confidentielle. DG, DAF, DRH, directeurs commerciaux et industriels, y compris en contexte sensible (restructuration, transformation, succession dirigeant). Notre approche directe et notre Plateforme Parcours Découverte permettent au dirigeant ciblé de comprendre votre projet avant tout engagement, ce qui sécurise la prise de poste sur ces missions à fort enjeu.",
  },
  {
    question: 'Proposez-vous une garantie sur les recrutements ?',
    answer:
      "Oui, une garantie chiffrée et opposable : replacement gratuit en cas de rupture dans les 6 mois suivant l'embauche, diagnostic post-mortem honnête (où ça a vraiment lâché), et 30 % du fee restitué si vous ne souhaitez pas de replacement. Pour que la garantie soit réelle, pas un argument commercial.",
  },
  {
    question: 'Quels sont vos tarifs pour un cabinet de recrutement à Aix-en-Provence ?',
    answer:
      "Sur le marché : 15 % à 30 % du salaire annuel brut chargé (7 500 € à 15 000 € pour un poste à 50 k€). Pour les profils rares ou de direction, le fee peut dépasser 20 000 €. Deux modèles disponibles : avec retainer (engagement initial qui inclut la production du Parcours Découverte) ou au succès uniquement (production Parcours facturée séparément). Tarifs présentés en transparence dès le premier appel.",
  },
  {
    question: 'Travaillez-vous avec des PME ou uniquement des grandes entreprises ?',
    answer:
      "Notre cœur de cible est la PME et l'ETI aixoises, de 20 à 500 collaborateurs. Nous travaillons aussi avec des scale-ups et des entreprises en transformation. Pour les très grands comptes, nous intervenons sur des missions ciblées (postes critiques, contextes sensibles) en collaboration avec leurs équipes RH internes.",
  },
];

const brandRiskStats = [
  {
    icon: Share2,
    metric: '72 %',
    title: 'partagent leur expérience candidat',
    description:
      "Chaque candidat de votre process en parle. À ses pairs, à ses collègues, sur LinkedIn, sur Glassdoor. Que vous l'embauchiez ou non. Source : Talent Board.",
  },
  {
    icon: TrendingDown,
    metric: '64 %',
    title: 'arrêtent d’acheter chez la marque',
    description:
      "Les candidats déçus ne vous oublient pas — ils boycottent. Pour une marque B2C aixoise (commerce, services, retail), le coût peut atteindre plusieurs centaines de milliers d'euros par an. Source : Talent Board.",
  },
  {
    icon: AlertTriangle,
    metric: '81 %',
    title: 'blacklistent une marque qui les ghoste',
    description:
      "Une candidature sans réponse, un process qui s'éteint en silence : c'est un client, un futur candidat et son réseau qui partent définitivement. Source : étude marché 2024.",
  },
];

const methodSteps = [
  {
    icon: FileText,
    title: 'Cadrage business du besoin',
    description:
      "Pas une fiche de poste générique : un diagnostic d'enjeu. Pourquoi ce poste, pour quel résultat, dans quel contexte managérial, avec quels critères de réussite mesurables.",
  },
  {
    icon: Compass,
    title: 'Réécriture engageante du brief candidat',
    description:
      "Nous transformons votre fiche de poste en mission claire, honnête, attirante. Le candidat doit comprendre POURQUOI ce poste vaut son temps avant de comprendre quoi y faire.",
  },
  {
    icon: Search,
    title: 'Sourcing chasse de tête + IA',
    description:
      "Approche directe sur les profils passifs aixois et PACA, scoring sémantique IA pour élargir intelligemment, qualification fine. La chasse senior pilote, l'IA accélère.",
  },
  {
    icon: Phone,
    title: 'Pré-qualification soignée',
    description:
      "Chaque candidat reçoit un appel de pré-qualification respectueux et structuré, en 30 minutes. C'est souvent leur premier contact avec votre entreprise — nous le traitons comme tel.",
  },
  {
    icon: Sparkles,
    title: 'Plateforme Parcours Découverte',
    description:
      "Les candidats short-listés traversent votre Parcours Découverte. Ils découvrent votre culture, votre équipe, votre quotidien réel, votre rémunération. Ils décident en connaissance de cause.",
  },
  {
    icon: CheckCircle2,
    title: 'Signature et ré-engagement',
    description:
      "Le candidat retenu se ré-engage explicitement à la sortie du Parcours. Plus de signature à l'aveugle, plus de no-show au jour J, plus de rétractation au dernier moment.",
  },
  {
    icon: ShieldCheck,
    title: 'Intégration et suivi 90 jours',
    description:
      "Onboarding personnalisé dès la signature, points de feedback RH et manager, objectifs suivis semaine par semaine. Le recrutement n'est solide qu'au 90e jour — pas à la signature.",
  },
];

const candidateCommitments = [
  {
    icon: Mail,
    title: 'Réponse écrite à 100 % des candidatures',
    description:
      "Sous 48 heures ouvrées. Aucun ghosting, jamais. Même un refus est une réponse soignée — c'est ce qui fait la différence dans la mémoire des candidats.",
  },
  {
    icon: MessageSquare,
    title: 'Brief honnête sur le poste, l’équipe, le contexte',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Le candidat reçoit la vraie réalité — y compris les contraintes — pour pouvoir décider en adulte.",
  },
  {
    icon: RefreshCw,
    title: 'Debrief structuré à chaque étape',
    description:
      "Le candidat sait pourquoi il avance, ou pourquoi il s'arrête. Avec des éléments concrets. Pour qu'il progresse, même s'il n'est pas retenu chez vous.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de surprise après la signature, plus de regret six mois après. Le candidat voit la réalité avant de s'engager — et n'avance que s'il est aligné.",
  },
];

const specialites = [
  {
    icon: Briefcase,
    title: 'Recrutement commercial à Aix',
    description:
      "Commerciaux terrain, KAM, business developers, directeurs régionaux. Le Pays d'Aix concentre un tissu B2B exigeant : SaaS, services pros, industrie. Nous évaluons la motivation, le projet professionnel et la compatibilité culturelle — pas uniquement le palmarès chiffré.",
  },
  {
    icon: Crown,
    title: 'Recrutement cadres et managers',
    description:
      "Responsables de service, directeurs opérationnels, cadres dirigeants. Nous menons des approches directes confidentielles sur des profils qui ne candidatent pas spontanément. Le PACA exige une lecture fine des codes locaux et de la mobilité géographique.",
  },
  {
    icon: Star,
    title: 'Tech et IT (Les Milles, Rousset)',
    description:
      "Développeurs, ingénieurs DevOps, data engineers, profils cybersécurité. Le Pôle d'Activités concentre l'écosystème tech aixois. Notre combo chasse + IA atteint les profils passifs sur-sollicités sur LinkedIn — la Plateforme Parcours Découverte fait la différence sur ces profils rares.",
  },
  {
    icon: Store,
    title: 'Retail, services et fonctions support',
    description:
      "Directeurs de magasin, chefs de secteur, RH, finance, ADV, juridique. Profils souvent sous-estimés dans leur impact sur la performance — nous leur accordons la même rigueur de qualification que les fonctions cœur de business.",
  },
];

const dividendStats = [
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits — y compris s'ils n'ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: ShoppingBag,
    metric: '× 4,4 M £',
    text:
      "C'est ce que Virgin Media a chiffré comme revenu perdu chaque année à cause de ses candidats refusés qui résiliaient leur abonnement.",
  },
  {
    icon: BarChart3,
    metric: '91 % vs 26 %',
    text:
      "des employeurs admettent l'impact de l'expérience candidat sur leurs ventes — 26 % seulement la mesurent. Le gap est votre opportunité.",
  },
];

const comparatif = [
  {
    critere: 'Plateforme Parcours Découverte',
    recruteurs: 'Environnement digital sur-mesure pour chaque candidat',
    classique: 'Aucun équivalent',
  },
  {
    critere: 'Expérience candidat',
    recruteurs: "Parcours immersif avant l'entretien",
    classique: 'Transmission de CV + entretien standard',
  },
  {
    critere: 'Sourcing',
    recruteurs: 'Approche directe + réseau + outils IA',
    classique: 'Jobboards et CVthèque',
  },
  {
    critere: 'Qualification',
    recruteurs: 'Entretiens structurés + évaluation motivationnelle',
    classique: 'Entretien de présélection rapide',
  },
  {
    critere: 'Suivi après embauche',
    recruteurs: 'Inclus — points à J+30 et J+90',
    classique: 'Rare ou facturable en option',
  },
  {
    critere: 'Garantie',
    recruteurs: 'Replacement < 6 mois + 30 % fee restitué',
    classique: 'Variable, conditions souvent floues',
  },
];

const guaranteeBullets = [
  {
    icon: RefreshCw,
    title: 'Replacement gratuit en cas de rupture < 6 mois',
    description:
      "Si votre nouvelle recrue rompt son contrat dans les 6 premiers mois, nous relançons un sourcing complet sans frais supplémentaires.",
  },
  {
    icon: FileText,
    title: 'Diagnostic post-mortem honnête',
    description:
      "Nous identifions où ça a vraiment lâché : projection candidat, package, management, contexte business. Pour ne pas refaire la même erreur.",
  },
  {
    icon: ShieldCheck,
    title: '30 % du fee restitué',
    description:
      "Si vous ne souhaitez pas de replacement, nous vous restituons 30 % du fee. Pour que la garantie soit réelle — pas un argument commercial.",
  },
];

const zones = [
  'Aix-en-Provence centre et périphérie',
  'Les Milles (zone d’activités)',
  'Rousset — Pôle technologique',
  'Gardanne',
  'Pertuis',
  'Vitrolles',
  'Marseille',
  'Salon-de-Provence',
  'PACA selon mission',
];

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  name: 'Recruteurs.io',
  description:
    "Cabinet de recrutement à Aix-en-Provence. Cadres, commerciaux, IT et fonctions support. Plateforme Parcours Découverte propriétaire.",
  url: 'https://recruteurs.io',
  email: 'contact@recruteurs.io',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '11 chemin du Mont Gibaou',
    addressLocality: 'Cassis',
    postalCode: '13260',
    addressCountry: 'FR',
  },
  areaServed: [
    { '@type': 'City', name: 'Aix-en-Provence' },
    { '@type': 'City', name: 'Les Milles' },
    { '@type': 'City', name: 'Rousset' },
    { '@type': 'City', name: 'Gardanne' },
    { '@type': 'City', name: 'Pertuis' },
    { '@type': 'City', name: 'Vitrolles' },
    { '@type': 'City', name: 'Salon-de-Provence' },
    { '@type': 'City', name: 'Marseille' },
    { '@type': 'AdministrativeArea', name: 'Bouches-du-Rhône' },
    { '@type': 'AdministrativeArea', name: 'PACA' },
  ],
  priceRange: '€€€',
  logo: 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png',
  sameAs: [
    'https://www.linkedin.com/company/recruteurs-io',
    'https://twitter.com/recruteurs_io',
    'https://www.facebook.com/recruteurs.io',
  ],
};

export default function CabinetRecrutementAixPage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Layout>
      <Helmet>
        <title>Cabinet de recrutement à Aix-en-Provence | Recruteurs.io</title>
        <meta
          name="description"
          content="Cabinet de recrutement à Aix-en-Provence : cadres, commerciaux, IT. Chasse de tête + méthode Parcours Découverte, garantie replacement sous 6 mois."
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Cabinet de recrutement à Aix-en-Provence', url: PAGE_URL },
        ]}
      />

      <FAQPageSchema questions={faqItems} />

      {/* HERO — layout centré + image Aix (2.png) en fond transparent */}
      <SectionBackground
        page="local-aix"
        section="lax-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef4e6] to-[#e0f2ff]"
      >
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none select-none flex items-center justify-center"
        >
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/2.png"
            alt=""
            className="w-full h-full object-cover opacity-15 md:opacity-20"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-white/30" />
        </div>

        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Recrutement local · Cadres · Tech · Pays d’Aix · PACA
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Cabinet de recrutement </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  à Aix-en-Provence
                </span>
              </h1>

              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Nous recrutons les profils qui durent&nbsp;: cadres, commerciaux,
                IT (Les Milles, Rousset) et fonctions support. Méthode rigoureuse,{' '}
                <strong>Plateforme Parcours Découverte</strong> avant signature,
                suivi post-intégration inclus. Connaissance fine du tissu PME/ETI
                aixois et des bassins d’emploi PACA.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/reservation')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Réserver un échange (15 min)
                </button>
                <button
                  onClick={() => navigate('/decouvrir')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  Voir le Parcours Découverte
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 2 — Marque employeur en jeu */}
      <SectionBackground page="local-aix" section="lax-stake" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le vrai coût d’un recrutement mal traité</span>
            <h2 className={h2Class}>
              Pour vous, c’est un poste à pourvoir.{' '}
              <span className={gradientText}>Pour 80 candidats aixois, c’est leur seul contact avec votre marque.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Sur un poste cadre à Aix-en-Provence, vous allez recevoir entre 50 et 100 candidatures.
              Vous en embaucherez une. Les autres ne disparaissent pas dans le silence — ils retournent
              à leur réseau, à leurs collègues, à LinkedIn, à Glassdoor. Et le Pays d’Aix est une
              boucle où tout le monde se connaît.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {brandRiskStats.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">{item.metric}</p>
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Ancrage local */}
      <SectionBackground page="local-aix" section="lax-anchor" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre ancrage régional</span>
            <h2 className={h2Class}>
              Le Pays d’Aix,{' '}
              <span className={gradientText}>un terrain que nous connaissons par cœur.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Tissu PME dense, écosystème tech (Les Milles, Rousset), services B2B et industrie :
              nous connaissons les spécificités sectorielles du Pays d’Aix et les attentes
              concrètes des dirigeants locaux.
            </p>
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 items-start">
              <div className="space-y-4">
                <p className={bodyClass}>
                  Notre approche : nous menons des missions de recrutement avec une vraie compréhension
                  du terrain, de la cible et du contexte d’entreprise. Cela change le résultat — les
                  candidats que nous vous présentons ont déjà compris votre environnement avant le
                  premier entretien.
                </p>
                <p className={bodyClass}>
                  Notre ancrage régional se traduit par un réseau de candidats actifs et passifs sur les
                  bassins d’emploi du Pays d’Aix — des profils souvent inaccessibles via les jobboards
                  traditionnels.
                </p>
              </div>
              <div className="space-y-4">
                <p className={bodyClass}>
                  Adresse opérationnelle : <strong>11 chemin du Mont Gibaou, 13260 Cassis</strong>.
                  Nous nous déplaçons sur Aix centre, Les Milles, Rousset, Pertuis, Gardanne, Marseille
                  et toute la PACA selon la nature de la mission.
                </p>
                <p className={bodyClass}>
                  Pour des postes de direction ou des profils rares, nous recrutons à l’échelle
                  nationale tout en gardant une connaissance fine des contextes locaux.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Méthode 7 temps */}
      <SectionBackground page="local-aix" section="lax-method" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode en 7 temps</span>
            <h2 className={h2Class}>
              Recruter mieux, pas juste plus vite.{' '}
              <span className={gradientText}>Exigeant côté business, premium côté candidat.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Engagements candidat */}
      <SectionBackground page="local-aix" section="lax-cx" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vivent vos candidats</span>
            <h2 className={h2Class}>
              Quatre promesses concrètes.{' '}
              <span className={gradientText}>Opposables. Vérifiables.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {candidateCommitments.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Spécialités */}
      <SectionBackground page="local-aix" section="lax-specialites" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos spécialités à Aix-en-Provence</span>
            <h2 className={h2Class}>
              Les fonctions où la qualité du recrutement{' '}
              <span className={gradientText}>change la performance.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specialites.map((s, index) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{s.title}</h3>
                  <p className={bodyClass}>{s.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Double dividende */}
      <SectionBackground page="local-aix" section="lax-dividend" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>
            <h2 className={h2Class}>
              Vous embauchez 1 candidat.{' '}
              <span className={gradientText}>Vous gagnez 79 ambassadeurs aixois.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">{stat.metric}</p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — Comparatif */}
      <SectionBackground page="local-aix" section="lax-vs" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Recruteurs.io vs cabinet classique</span>
            <h2 className={h2Class}>
              Ce qui différencie notre approche n’est pas le discours.{' '}
              <span className={gradientText}>C’est ce que nous faisons à chaque étape.</span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto rounded-[2rem] ring-1 ring-neutral-200 overflow-hidden bg-white">
            <div className="hidden md:grid grid-cols-[1fr_1fr_1fr] bg-neutral-900 text-white">
              <div className="p-5 text-xs font-semibold uppercase tracking-widest">Critère</div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest bg-gradient-to-r from-[#d03484] to-[#d47254]">recruteurs.io</div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest text-neutral-400">Cabinet classique</div>
            </div>

            {comparatif.map((row, idx) => (
              <div
                key={row.critere}
                className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] ${
                  idx !== comparatif.length - 1 ? 'border-b border-neutral-100' : ''
                } ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfaf8]'}`}
              >
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider md:hidden mb-1">Critère</p>
                  <p className="text-base font-semibold text-[#30475e]">{row.critere}</p>
                </div>
                <div className="p-5 md:p-6 bg-gradient-to-r from-[#fde7f2]/40 to-[#fde2d3]/40">
                  <p className="text-sm font-semibold text-[#d03484] uppercase tracking-wider md:hidden mb-1">recruteurs.io</p>
                  <span className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base font-semibold text-[#30475e]">{row.recruteurs}</span>
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wider md:hidden mb-1">Cabinet classique</p>
                  <p className="text-base text-neutral-500">{row.classique}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — Garantie */}
      <SectionBackground page="local-aix" section="lax-guarantee" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre garantie</span>
            <h2 className={h2Class}>
              Si ça ne tient pas dans les 6 mois,{' '}
              <span className={gradientText}>on recommence.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guaranteeBullets.map((g, index) => {
              const Icon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{g.title}</h3>
                  <p className={bodyClass}>{g.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — Zones */}
      <SectionBackground page="local-aix" section="lax-zones" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Zones couvertes</span>
            <h2 className={h2Class}>
              Pays d’Aix et{' '}
              <span className={gradientText}>bassins d’emploi PACA.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Nous intervenons sur l’ensemble du Pays d’Aix et de la{' '}
              <a href="/cabinet-recrutement-paca" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">PACA</a>{' '}
              selon la nature de la mission.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 max-w-4xl mx-auto mb-10">
            {zones.map((zone) => (
              <div key={zone} className="flex items-center gap-2 bg-[#fbfaf8] rounded-xl px-4 py-3 ring-1 ring-neutral-200">
                <MapPin className="w-4 h-4 text-[#d03484] flex-shrink-0" />
                <span className="text-sm font-medium text-neutral-800">{zone}</span>
              </div>
            ))}
          </div>

          <div className="rounded-[2rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={bodyClass}>
              Pour des postes de direction, des profils rares ou des missions de chasse de tête, nous
              recrutons à l’échelle nationale tout en gardant une connaissance fine des contextes
              locaux. Si votre siège est à Aix mais que le poste est basé à{' '}
              <a href="/cabinet-recrutement-marseille" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">Marseille</a>,
              nous intervenons sans contrainte de périmètre.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 11 — Logos clients (wall créatif partagé) */}
      <ClientLogosWall
        page="local-aix"
        section="lax-trust"
        className="bg-[#fbfaf8]"
      />

      {/* SECTION 12 — Vidéo */}
      <SectionBackground page="local-aix" section="lax-press" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}><PlayCircle className="w-4 h-4 mr-2" />Vu à la télé</span>
            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 13 — FAQ */}
      <SectionBackground page="local-aix" section="lax-faq" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Questions fréquentes</span>
            <h2 className={h2Class}>
              Sur le recrutement{' '}
              <span className={gradientText}>à Aix-en-Provence.</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="/infos/questions" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
              Voir toutes les questions fréquentes →
            </a>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 14 — CTA */}
      <SectionBackground page="local-aix" section="lax-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Parlons de votre recrutement{' '}
              <span className={gradientText}>à Aix-en-Provence.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Une conversation directe, sans engagement, pour voir si nous sommes le bon partenaire.
              <br /><br />
              Nous intervenons sur <strong>Aix, Pays d’Aix, Marseille et l’ensemble de la PACA</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Réserver un échange (15 min)
              </button>
              <button
                onClick={() => setIsHubSpotModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Nous écrire
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </Layout>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-[#30475e] text-base leading-snug">{question}</span>
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-neutral-700 leading-relaxed border-t border-neutral-100 pt-4 text-base">
          {answer}
        </div>
      )}
    </div>
  );
}
